"use client";

import React, { useCallback, useState } from "react";
import { z, z as zod } from "zod";
import { useFieldArray, UseFormReturn, useWatch } from "react-hook-form";
import {
	Box,
	Card,
	CardContent,
	Stack,
	Typography,
	Button,
	IconButton,
	DialogTitle,
	DialogActions,
	Dialog,
} from "@mui/material";
import { schemaHelper } from "@onair/minimal-ui/hook-form";
import { useResponsive } from "@onair/minimal-ui/hooks/use-responsive";

import dayjs from "@onair/utils/dayjs";

import { WizardSchemaType } from "./index.tsx";
import PreviewInfo from "./preview/info.tsx";
import CardWizard from "./card-wizard.tsx";
import { Iconify } from "@onair/minimal-ui/iconify";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableEventSession from "./event-sessions/sortable-event-session.tsx";
import { CoHostType } from "@onair/constants";

interface PropTypes {
	methods: UseFormReturn<WizardSchemaType>;
	navigator?: React.ReactNode;
}

export const EVENT_SESSION_DEFAULT_VALUE = {
	startDate: null,
	endDate: null,
	name: undefined,
	content: undefined,
	banner: undefined,
	coHostType: CoHostType.SYSTEM,
	coHost: undefined,
	coHostEntity: undefined,
	_coHostSearch: "",
	_id: undefined,
};

export const EVENT_SESSION_PRICING_DEFAULT_VALUE = {
	roomProvider: undefined,
	roomUrl: undefined,
	roomInfo: undefined,
	ticketTiers: [{}],
};

export const EventSessionTimelineSchema = zod.object({
	startDate: schemaHelper
		.date({
			message: {
				required_error: "Vui lòng nhập thời gian bắt đầu của sự kiện",
			},
		})
		.refine((data) => dayjs().isBefore(data), {
			message: "Thời gian bắt đầu phải lớn hơn hiện tại",
		}),
	endDate: schemaHelper
		.date({
			message: {
				required_error: "Vui lòng nhập thời gian kết thúc của sự kiện",
			},
		})
		.refine((data) => dayjs().isBefore(data), {
			message: "Thời gian kết thúc phải lớn hơn thời gian hiện tại.",
		}),
	name: zod
		.string({ message: "Vui lòng nhập tên sự kiện!" })
		.min(1, { message: "Vui lòng nhập tên sự kiện!" })
		.max(100, { message: "Vui lòng nhập tối đa 100 ký tự" }),
	content: zod.string().optional(),
	banner: zod.custom<File | string | null>(),
	// Co-host
	coHostType: zod.nativeEnum(CoHostType).optional(),
	coHost: zod
		.object({
			id: zod.string().optional(),
			name: zod.string().optional(),
			email: zod.union([
				zod.literal(""),
				zod.string().email({
					message: "Vui lòng nhập email hợp lệ",
				}),
			]),
		})
		.optional(),
	address: zod.string().optional(),
	coHostEntity: z.any().optional(),
	_coHostSearch: z.string().optional(),
	//
	_id: z.string().optional(),
});

export const EventSessionPricingSchema = zod.object({
	roomProvider: zod.any(),
	roomUrl: zod.string().url().nullable().optional(),
	roomInfo: zod
		.object({
			id: zod.string().optional(),
			password: zod.string().optional(),
		})
		.optional(),
	ticketTiers: zod.array(zod.any()).optional(),
	address: zod.string().optional(),
});

export const StepTimelineSchema = zod.object({
	sessions: zod
		.array(
			EventSessionTimelineSchema.refine(
				(data) => {
					return (
						!data.endDate ||
						!data.startDate ||
						dayjs(data.endDate).isAfter(data.startDate)
					);
				},
				{
					message: "Thời gian kết thúc phải lớn hơn thời gian bắt đầu",
					path: ["endDate"],
				},
			)
				.refine(
					(data) => {
						if (data.coHostType === CoHostType.SYSTEM) {
							return true;
						}

						if (data.coHost?.name && !data.coHost?.email) {
							return false;
						}

						return true;
					},
					{
						message: "Vui lòng nhập email của Co-host",
						path: ["coHost", "email"],
					},
				)
				.refine(
					(data) => {
						if (data.coHostType === CoHostType.SYSTEM) {
							return true;
						}

						if (data.coHost?.email && !data.coHost?.name) {
							return false;
						}

						return true;
					},
					{
						message: "Vui lòng nhập tên của Co-host",
						path: ["coHost", "name"],
					},
				),
		)
		.min(2, { message: "Vui lòng thêm ít nhất 2 sự kiện" }),
});

export type StepTimelineSchemaType = zod.infer<typeof StepTimelineSchema>;

const StepTimeline: React.FC<PropTypes> = ({ methods, navigator }) => {
	const { resetField, control } = methods;

	const mdDown = useResponsive("down", "md");
	const [showWarningModal, setShowWarningModal] = useState(false);
	const { fields, append, remove, swap } = useFieldArray({
		control,
		name: "stepTimeline.sessions",
	});

	const {
		append: appendPricing,
		remove: removePricing,
		swap: swapPricing,
	} = useFieldArray({
		control,
		name: "stepPricing.sessions",
	});

	const data = useWatch({
		control,
	});

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;

			if (active.id !== over?.id) {
				const oldIndex = fields.findIndex((field) => field.id === active.id);
				const newIndex = fields.findIndex((field) => field.id === over?.id);

				swap(oldIndex, newIndex);
				swapPricing(oldIndex, newIndex);
			}
		},
		[fields, swap, swapPricing],
	);

	const handleRemove = useCallback(
		(index: number) => {
			if (fields.length <= 2) {
				setShowWarningModal(true);
				return;
			}

			resetField(`stepTimeline.sessions.${index}`);
			resetField(`stepPricing.sessions.${index}`);
			remove(index);
			removePricing(index);
		},
		[fields.length, remove, resetField],
	);

	const handleCloseWarningModal = useCallback(() => {
		setShowWarningModal(false);
	}, []);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	return (
		<Stack direction="row" spacing={3}>
			<Box flex={1}>
				<CardWizard>
					<CardContent>
						<Stack spacing={3}>
							<Stack
								spacing={{
									xs: 3,
									md: 2,
								}}
							>
								<Typography fontWeight={600} variant="body1">
									Chi tiết chuỗi sự kiện
								</Typography>
								<DndContext
									sensors={sensors}
									collisionDetection={closestCenter}
									onDragEnd={handleDragEnd}
								>
									<SortableContext
										items={fields}
										strategy={verticalListSortingStrategy}
									>
										{fields.map((eventSession, index) => (
											<SortableEventSession
												key={eventSession.id}
												index={index}
												eventSession={eventSession}
												methods={methods}
												onRemove={handleRemove}
											/>
										))}
									</SortableContext>
								</DndContext>
								<Stack
									mt={1}
									justifyContent="space-between"
									spacing={1.5}
									alignItems="center"
									direction="row"
								>
									<Box
										width="35%"
										height="1px"
										bgcolor="rgba(0, 80, 255, 0.48)"
									/>
									<Button
										variant="outlined"
										color="primary"
										sx={{
											borderRadius: 100,
											flexGrow: 1,
											width: "40%",
										}}
										onClick={() => {
											// @ts-ignore
											append(EVENT_SESSION_DEFAULT_VALUE);
											// @ts-ignore
											appendPricing(EVENT_SESSION_PRICING_DEFAULT_VALUE);
										}}
										startIcon={
											<Iconify icon="eva:plus-circle-outline" width={20} />
										}
									>
										Thêm mới
									</Button>
									<Box
										width="35%"
										height="1px"
										bgcolor="rgba(0, 80, 255, 0.48)"
									/>
								</Stack>
							</Stack>
						</Stack>
					</CardContent>
				</CardWizard>
				{navigator}
			</Box>
			<Dialog open={showWarningModal} maxWidth="xs" fullWidth scroll="paper">
				<DialogTitle
					display="flex"
					sx={{
						p: 3,
						pr: 2,
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Typography fontWeight={700} variant="h6">
						Thông báo
					</Typography>
					<IconButton aria-label="close" onClick={handleCloseWarningModal}>
						<Iconify
							sx={{
								color: "action.active",
							}}
							icon="eva:close-fill"
							width={18}
						/>
					</IconButton>
				</DialogTitle>
				<Box px={3}>
					<Typography variant="body2">
						Chuỗi sự kiện phải bao gồm ít nhất 2 sự kiện trở lên.
					</Typography>
				</Box>
				<DialogActions>
					<Button
						color="inherit"
						variant="outlined"
						size="medium"
						onClick={() => {
							handleCloseWarningModal();
						}}
					>
						Đóng
					</Button>
				</DialogActions>
			</Dialog>
			{!mdDown && <PreviewInfo data={data as WizardSchemaType} />}
		</Stack>
	);
};

export default StepTimeline;
